(() => {
  'use strict';
  if (window.DaLucaCartEditor) return;
  const configNode = document.getElementById('DaLucaCartEditConfig');
  if (!configNode || !window.DaLucaCartEditPlan) return;
  const text = JSON.parse(window.DaLucaI18n.sourceText(configNode));
  const planner = window.DaLucaCartEditPlan;
  const tokenKey = planner.tokenProperty;
  const storageKey = 'daluca-cart-edit-v1';
  const editKey = new URLSearchParams(location.search).get('dl_edit');
  const rootUrl = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const pause = ms => new Promise(resolve => setTimeout(resolve, ms));
  const norm = value => String(value ?? '').replace(/\*/g, '').replace(/\s+/g, ' ').trim();
  const choice = value => norm(value).replace(/\s*\(\+\s*\$\s*[\d,.]+(?:\s*USD)?\)\s*$/i, '');
  let context = null;
  let saving = false;
  let restored = false;
  let restoringChoices = false;
  let record = null;
  let recovery = null;
  let banner = null;
  let purchase = null;
  let dialog = null;
  let uiObserver = null;
  let uiQueued = false;
  let pageEpoch = 0;
  let recoveryUncertain = false;
  let canKeepOriginal = false;
  let dialogCanDismiss = false;
  let guardQueued = false;
  const activeRequests = new Set();
  const guardedElements = new Map();
  const checkoutSelector = '[name="checkout"],.checkout-button,.shopify-payment-button,.additional-checkout-buttons,.cart__dynamic-checkout-buttons,shopify-accelerated-checkout,shopify-accelerated-checkout-cart,[data-shopify-buttoncontainer]';
  const cartMutationSelector = '#Cart quantity-selector,#Cart cart-remove,#Cart .daluca-cart-edit-link,#Cart-Drawer quantity-selector,#Cart-Drawer cart-remove,#Cart-Drawer .daluca-cart-edit-link';
  const additionSelector = '#main-content .single-add-to-cart-button,#main-content .daluca-proxy-add-to-cart,#main-content .daluca-care-proxy-v35__add,#main-content .daluca-pdp-upsell__add,#main-content [data-daluca-accessory-addon-group] button,#main-content .daluca-ready-addon-row button';

  function error(code) {
    const result = new Error(code);
    result.code = code;
    return result;
  }

  async function request(path, payload) {
    const controller = new AbortController();
    activeRequests.add(controller);
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(rootUrl() + path, {
        method: payload ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(payload ? { body: JSON.stringify(payload) } : {}),
        signal: controller.signal
      });
      const result = await response.json();
      if (!response.ok || Number(result.status) >= 400 || result.errors) {
        const rejected = error('CART_REQUEST_FAILED');
        rejected.definitive = true;
        throw rejected;
      }
      return result;
    } finally {
      clearTimeout(timeout);
      activeRequests.delete(controller);
    }
  }

  function compactCart(cart) {
    return {
      currency: cart.currency,
      items: cart.items.map(line => ({
        ...planner.reference(line),
        product_title: line.product_title,
        price: Number(line.price),
        original_price: Number(line.original_price ?? line.price),
        original_line_price: Number(line.original_line_price ?? line.line_price)
      }))
    };
  }

  function assertCurrent(transaction, epoch) {
    if (epoch != null && epoch !== pageEpoch) throw error('STALE_TRANSACTION');
    const current = readRecord();
    if (!current || !transaction || current.token !== transaction.token) throw error('STALE_TRANSACTION');
    record = current;
    return current;
  }

  function assertIdle(epoch) {
    if (epoch !== pageEpoch || readRecord()) throw error('STALE_TRANSACTION');
  }

  async function transactionRequest(transaction, epoch, path, payload) {
    assertCurrent(transaction, epoch);
    try {
      const result = await request(path, payload);
      assertCurrent(transaction, epoch);
      return result;
    } catch (failure) {
      assertCurrent(transaction, epoch);
      throw failure;
    }
  }

  function persist(next, initial) {
    const current = readRecord();
    if (initial ? !!current : !current || current.token !== next.token) throw error('STALE_TRANSACTION');
    try {
      next = { ...current, ...next, revision: Number(current?.revision || 0) + 1 };
      sessionStorage.setItem(storageKey, JSON.stringify(next));
      if (JSON.parse(sessionStorage.getItem(storageKey)).token !== next.token) throw error('STORAGE');
      record = next;
    } catch (_) {
      throw error('STORAGE');
    }
    syncRecoveryUi();
  }

  function readRecord() {
    try {
      const value = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      if (!value) return null;
      if (value.version !== 1 || !value.token || !value.before?.items || !value.originalKey) throw error('INVALID_SESSION');
      return value;
    } catch (_) {
      return null;
    }
  }

  function clearRecord(transaction) {
    assertCurrent(transaction);
    sessionStorage.removeItem(storageKey);
    record = null;
    recoveryUncertain = false;
    canKeepOriginal = false;
    syncRecoveryUi();
  }

  function visibleMessage(message) {
    if (banner) window.DaLucaI18n.setText(banner.querySelector('[data-daluca-edit-message]'), message);
  }

  function showDialog(message, canCheck) {
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'daluca-cart-edit-dialog';
      dialog.setAttribute('aria-labelledby', 'DaLucaCartEditDialogTitle');
      const heading = document.createElement('h2');
      heading.id = 'DaLucaCartEditDialogTitle';
      window.DaLucaI18n.setText(heading, text.title);
      const copy = document.createElement('p');
      copy.setAttribute('role', 'status');
      copy.setAttribute('aria-live', 'polite');
      dialog.append(heading, copy);
      dialog.addEventListener('cancel', event => {
        event.preventDefault();
        if (dialogCanDismiss) dismissRecovery(false);
      });
      document.body.appendChild(dialog);
    }
    window.DaLucaI18n.setText(dialog.querySelector('p'), message);
    dialogCanDismiss = !!canCheck;
    dialog.querySelector('.daluca-cart-edit-actions')?.remove();
    if (canCheck) {
      const actions = document.createElement('div');
      actions.className = 'daluca-cart-edit-actions';
      const check = document.createElement('button');
      check.type = 'button';
      check.className = 'button full';
      window.DaLucaI18n.setText(check, text.retry);
      check.addEventListener('click', () => resume(record, { interactive: true }));
      actions.appendChild(check);
      if (canKeepOriginal) actions.appendChild(recoveryButton(text.keepOriginal, () => resume(record, { keepOriginal: true, interactive: true })));
      actions.appendChild(recoveryButton(text.viewCart, () => dismissRecovery(true), true));
      dialog.appendChild(actions);
    }
    if (!dialog.open) dialog.showModal();
  }

  function hideDialog() {
    if (dialog?.open) dialog.close();
    dialogCanDismiss = false;
  }

  function recoveryButton(label, callback, secondary) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = secondary ? 'button outline' : 'button';
    window.DaLucaI18n.setText(button, label);
    button.addEventListener('click', callback);
    return button;
  }

  function isCheckoutUrl(value) {
    try { return /\/checkouts?(?:\/|$)/i.test(new URL(value, location.href).pathname); }
    catch (_) { return false; }
  }

  function recoveryNotice(compact) {
    const notice = document.createElement('section');
    notice.className = 'daluca-cart-recovery' + (compact ? ' daluca-cart-recovery--compact' : '');
    notice.dataset.dalucaCartRecovery = 'true';
    notice.tabIndex = -1;
    const heading = document.createElement('h2');
    window.DaLucaI18n.setText(heading, text.recoveryTitle);
    const copy = document.createElement('p');
    copy.dataset.dalucaRecoveryMessage = 'true';
    copy.setAttribute('role', 'status');
    copy.setAttribute('aria-live', 'polite');
    const actions = document.createElement('div');
    actions.className = 'daluca-cart-edit-actions';
    const check = recoveryButton(text.retry, () => resume(record, { interactive: true }));
    check.dataset.dalucaRecoveryCheck = 'true';
    const keep = recoveryButton(text.keepOriginal, () => resume(record, { keepOriginal: true, interactive: true }), true);
    keep.dataset.dalucaRecoveryKeep = 'true';
    actions.append(check, keep);
    notice.append(heading, copy, actions);
    return notice;
  }

  function syncRecoveryUi() {
    const pending = !!record;
    document.documentElement.toggleAttribute('data-daluca-cart-unresolved', pending);
    const selectors = [checkoutSelector, cartMutationSelector];
    if (recoveryUncertain) selectors.push(additionSelector);
    const targets = new Set(pending ? document.querySelectorAll(selectors.join(',')) : []);
    if (pending) document.querySelectorAll('a[href]').forEach(link => {
      if (isCheckoutUrl(link.href)) targets.add(link);
    });
    guardedElements.forEach((prior, element) => {
      if (targets.has(element)) return;
      element.inert = prior.inert;
      if ('disabled' in element) element.disabled = prior.disabled;
      if (prior.aria == null) element.removeAttribute('aria-disabled');
      else element.setAttribute('aria-disabled', prior.aria);
      element.classList.remove('daluca-cart-recovery-locked');
      guardedElements.delete(element);
    });
    targets.forEach(element => {
      if (!guardedElements.has(element)) guardedElements.set(element, {
        inert: !!element.inert, disabled: !!element.disabled, aria: element.getAttribute('aria-disabled')
      });
      if (!element.inert) element.inert = true;
      if ('disabled' in element && !element.disabled) element.disabled = true;
      if (element.getAttribute('aria-disabled') !== 'true') element.setAttribute('aria-disabled', 'true');
      if (!element.classList.contains('daluca-cart-recovery-locked')) element.classList.add('daluca-cart-recovery-locked');
    });
    if (!pending) {
      document.querySelectorAll('[data-daluca-cart-recovery]').forEach(notice => notice.remove());
      return;
    }
    const main = document.getElementById('Cart') || document.getElementById('main-content');
    const mounts = [main, document.querySelector('#Cart-Drawer .cart-drawer-buttons')].filter(Boolean);
    mounts.forEach((mount, index) => {
      let notice = Array.from(mount.children).find(child => child.hasAttribute('data-daluca-cart-recovery'));
      if (!notice) { notice = recoveryNotice(index > 0); mount.prepend(notice); }
      const message = recoveryUncertain ? text.unresolved : text.checking;
      const copy = notice.querySelector('[data-daluca-recovery-message]');
      if (window.DaLucaI18n.sourceText(copy) !== message) window.DaLucaI18n.setText(copy, message);
      const check = notice.querySelector('[data-daluca-recovery-check]');
      if (check.disabled !== !!recovery) check.disabled = !!recovery;
      const keep = notice.querySelector('[data-daluca-recovery-keep]');
      if (keep.hidden !== !canKeepOriginal) keep.hidden = !canKeepOriginal;
      if (keep.disabled !== !!recovery) keep.disabled = !!recovery;
    });
  }

  function dismissRecovery(goToCart) {
    const current = readRecord();
    if (!current) { reconcileJournal(); return; }
    persist({ ...current, dismissed: true });
    hideDialog();
    syncRecoveryUi();
    const cartUrl = new URL(text.cartUrl, location.origin);
    if (goToCart && location.pathname.replace(/\/$/, '') !== cartUrl.pathname.replace(/\/$/, '')) {
      location.assign(cartUrl.pathname + cartUrl.search);
      return;
    }
    document.querySelector('[data-daluca-cart-recovery]')?.focus({ preventScroll: true });
  }

  function reconcileJournal() {
    record = readRecord();
    canKeepOriginal = false;
    if (!record) {
      saving = false;
      restored = false;
      recoveryUncertain = false;
      hideDialog();
      if (banner) visibleMessage(text.changed);
      applyUi();
      syncRecoveryUi();
      return;
    }
    saving = true;
    recoveryUncertain = !!record.dismissed;
    syncRecoveryUi();
    if (!recovery) resume(record);
  }

  function formProperties(form) {
    const result = {};
    new FormData(form).forEach((value, name) => {
      const match = name.match(/^properties\[(.*)\]$/);
      if (match && typeof value === 'string') result[match[1]] = value;
    });
    return result;
  }

  function preparedFormMatches(form, transaction) {
    return !!form && !!transaction && formProperties(form)[tokenKey] === transaction.token;
  }

  function feeSignature(properties) {
    const value = properties?._gpo_addon_products;
    if (!value || value === '[]' || value === '{}') return '';
    try { return planner.signature(JSON.parse(value)); }
    catch (_) { return null; }
  }

  function publicMatches(line, expected, propertyAliases) {
    return Object.entries(expected || {}).every(([key, value]) => {
      if (key[0] === '_') return true;
      if (Object.prototype.hasOwnProperty.call(line.properties || {}, key)) return choice(line.properties[key]) === choice(value);
      const observed = propertyAliases?.[key];
      if (!Array.isArray(observed)) return false;
      const aliases = observed.filter(alias => alias[0] !== '_' && Object.prototype.hasOwnProperty.call(line.properties || {}, alias));
      return aliases.length > 0 && aliases.every(alias => choice(line.properties[alias]) === choice(value));
    });
  }

  function feeDelta(before, after, replacement) {
    const group = replacement.properties?._gpo_product_group;
    if (!group) return 0;
    const prior = new Map();
    before.items.forEach(line => {
      const key = planner.fingerprint(line);
      prior.set(key, (prior.get(key) || 0) + Number(line.quantity));
    });
    const current = new Map();
    after.items.filter(line => String(line.properties?._gpo_parent_product_group || '') === String(group)).forEach(line => {
      const key = planner.fingerprint(line);
      const price = Number(line.original_price ?? line.price);
      const entry = current.get(key) || { quantity: 0, price };
      if (!Number.isSafeInteger(price) || price < 0 || entry.price !== price) throw error('CART_CHANGED');
      entry.quantity += Number(line.quantity);
      current.set(key, entry);
    });
    let total = 0;
    current.forEach((entry, key) => {
      const delta = entry.quantity - (prior.get(key) || 0);
      if (delta < 0) throw error('CART_CHANGED');
      total += entry.price * delta;
    });
    return total;
  }

  function originalFee(cart, parent) {
    const group = parent.properties?._gpo_product_group;
    if (!group) return 0;
    const quantity = cart.items.filter(line => String(line.properties?._gpo_product_group || '') === String(group))
      .reduce((sum, line) => sum + Number(line.quantity), 0);
    if (!quantity) throw error('CART_CHANGED');
    return cart.items.filter(line => String(line.properties?._gpo_parent_product_group || '') === String(group))
      .reduce((sum, line) => sum + Number(line.original_price ?? line.price) * Number(line.quantity), 0) / quantity;
  }

  function replacementFor(cart, transaction) {
    const matches = cart.items.filter(line => line.properties?.[tokenKey] === transaction.token);
    if (matches.length > 1) throw error('REPLACEMENT_AMBIGUOUS');
    return matches[0] || null;
  }

  function catalogBasePrice(productId, variantId, currency) {
    const pricing = text.productPricing;
    if (!pricing || String(pricing.productId) !== String(productId) ||
        pricing.currency !== currency) return null;
    const value = pricing.variants?.[String(variantId)];
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }

  function verifyReplacement(cart, transaction, requireFees) {
    const line = replacementFor(cart, transaction);
    if (!line) return null;
    if (String(line.product_id) !== String(transaction.productId)) throw error('REPLACEMENT_PRODUCT_MISMATCH');
    if (String(line.variant_id) !== String(transaction.variantId)) throw error('REPLACEMENT_VARIANT_MISMATCH');
    if (Number(line.quantity) !== Number(transaction.quantity)) throw error('REPLACEMENT_QUANTITY_MISMATCH');
    const expectedProperties = transaction.verificationProperties || transaction.properties;
    if (!publicMatches(line, expectedProperties, transaction.propertyAliases)) {
      const fields = Object.keys(expectedProperties).filter(key => key[0] !== '_' && choice(line.properties?.[key]) !== choice(expectedProperties[key]));
      console.warn('[DaLuca cart edit] Unconfirmed option labels:', fields.join(', '));
      throw error('REPLACEMENT_PROPERTIES_MISMATCH');
    }
    if (requireFees) {
      if (cart.currency !== transaction.before.currency) throw error('CURRENCY_CHANGED');
      const base = transaction.basePriceCents;
      const parentTotal = Number(line.original_line_price ??
        Number(line.original_price ?? line.price) * Number(line.quantity));
      if (!Number.isSafeInteger(base) || base < 0 ||
          !Number.isSafeInteger(parentTotal) || parentTotal < 0) return null;
      // Globo can include the option fee in the parent or in linked child lines.
      // Compare both against the selected variant's independent catalog price.
      const expected = (base + transaction.feeCents) * transaction.quantity;
      if (!Number.isSafeInteger(expected) ||
          parentTotal + feeDelta(transaction.before, cart, line) !== expected) return null;
    }
    return line;
  }

  function hidden(form, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = String(value);
    input.dataset.dalucaEditProperty = 'true';
    form.appendChild(input);
    return input;
  }

  function prepareForm(form, properties, token, quantity) {
    form.querySelectorAll('[data-daluca-edit-property]').forEach(input => input.remove());
    Object.entries(properties).forEach(([key, value]) => {
      if (key.startsWith('_gpo_') || key === tokenKey) return;
      const name = 'properties[' + key + ']';
      if (!Array.from(form.elements).some(input => input.name === name)) hidden(form, name, value);
    });
    hidden(form, 'properties[' + tokenKey + ']', token);
    const inputs = Array.from(form.elements).filter(input => input.name === 'quantity');
    if (inputs.length) inputs.forEach(input => { input.value = String(quantity); });
    else hidden(form, 'quantity', quantity);
  }

  function mergedProperties(original, descriptor, form) {
    const result = { ...(original.properties || {}) };
    const managed = new Set(descriptor.managedKeys || []);
    Object.keys(result).forEach(key => {
      if (managed.has(key) || key.startsWith('_gpo_') || key === tokenKey) delete result[key];
    });
    const fresh = formProperties(form);
    if (context?.custom) Object.entries(descriptor.nativeProperties || {}).forEach(([key, value]) => {
      if (key[0] !== '_' && !Object.prototype.hasOwnProperty.call(fresh, key)) fresh[key] = value;
    });
    Object.assign(result, fresh, descriptor.properties || {});
    (descriptor.managedKeys || []).forEach(key => {
      if (!(key in fresh) && !(key in (descriptor.properties || {}))) delete result[key];
    });
    // The custom builder retires this legacy option; care products are separate cart lines.
    if (context?.custom) Object.keys(result).forEach(key => {
      if (/venetian\s*cream/i.test(key)) delete result[key];
    });
    if (context?.custom) Object.entries(descriptor.propertyAliases || {}).forEach(([canonical, aliases]) => {
      if (!Array.isArray(aliases) || !Object.prototype.hasOwnProperty.call(result, canonical)) return;
      const observed = aliases.filter(alias => alias !== canonical && alias[0] !== '_' &&
        Object.prototype.hasOwnProperty.call(fresh, alias) && Object.prototype.hasOwnProperty.call(result, alias));
      if (observed.length > 0 && observed.every(alias => choice(result[alias]) === choice(result[canonical]))) delete result[canonical];
    });
    delete result[tokenKey];
    return result;
  }

  function serviceSignature(properties) {
    return JSON.stringify(Object.keys(properties || {}).sort().reduce((out, key) => {
      if (properties[key] !== '' && properties[key] != null) out[key] = String(properties[key]);
      return out;
    }, {}));
  }

  function linkedServices(cart, original) {
    const signature = serviceSignature(original.properties);
    const careHandles = new Set(['venetian-cream-3oz', 'venetian-cream-4oz', 'venetian-cream-leather-shine-cloth', 'extra-standard-spring-bars', 'spring-bar-buckle-removal-tool', 'spring-bar-tool']);
    return cart.items.filter(line => {
      const p = line.properties || {};
      if (careHandles.has(line.handle) && !p._gpo_parent_product_group && !line.parent_relationship?.parent_key) return false;
      if (p['_DaLuca linked upgrade'] !== 'true' ||
          String(p['_DaLuca parent variant']) !== String(original.variant_id)) return false;
      try { return serviceSignature(JSON.parse(p['_DaLuca parent options'])) === signature; }
      catch (_) { throw error('UNSUPPORTED_LINKED_UPGRADE'); }
    });
  }

  function checkedServices(cart, original, allowAnyService) {
    const services = linkedServices(cart, original);
    if (services.length && (services.length !== 1 || cart.items.filter(line =>
      String(line.variant_id) === String(original.variant_id) && serviceSignature(line.properties) === serviceSignature(original.properties)).length !== 1)) {
      throw error('UNSUPPORTED_LINKED_UPGRADE');
    }
    if (services.some(service => Number(service.quantity) !== Number(original.quantity))) {
      throw error('LINKED_SERVICE_QUANTITY_MISMATCH');
    }
    if (!allowAnyService && services.some(service => String(service.variant_id) !== '51797092368690')) {
      throw error('UNSUPPORTED_LINKED_UPGRADE');
    }
    return services;
  }

  function serviceReference(service, replacement) {
    return { ...planner.reference(service), key: '', properties: {
      ...service.properties,
      '_DaLuca parent variant': String(replacement.variant_id),
      '_DaLuca parent options': serviceSignature(replacement.properties)
    } };
  }

  function parentCart(transaction, cart, allowRelinked = true) {
    const original = planner.identify(transaction.before, transaction.originalKey);
    const services = checkedServices(transaction.before, original, false);
    if (!services.length) return cart;
    const replacement = allowRelinked ? replacementFor(cart, transaction) : null;
    const excluded = new Set();
    services.forEach(service => {
      const current = (replacement && planner.identify(cart, serviceReference(service, replacement))) ||
        planner.identify(cart, planner.reference(service));
      if (!current || lineMoney(current) !== lineMoney(service)) throw error('UPDATE_UNCONFIRMED');
      excluded.add(current);
    });
    return { ...cart, items: cart.items.filter(line => !excluded.has(line)) };
  }

  async function relinkServices(transaction, cart, replacement, epoch) {
    const original = planner.identify(transaction.before, transaction.originalKey);
    const services = linkedServices(transaction.before, original);
    for (const service of services) {
      const properties = {
        ...service.properties,
        '_DaLuca parent variant': String(replacement.variant_id),
        '_DaLuca parent options': serviceSignature(replacement.properties)
      };
      const desired = { ...planner.reference(service), key: '', properties };
      if (planner.identify(cart, desired)) continue;
      const current = planner.identify(cart, planner.reference(service));
      if (!current) throw error('CART_CHANGED');
      try {
        await transactionRequest(transaction, epoch, 'cart/change.js', { id: current.key, quantity: current.quantity, properties });
      } finally {
        cart = await transactionRequest(transaction, epoch, 'cart.js');
      }
      if (!planner.identify(cart, desired)) throw error('VERIFY_FAILED');
    }
    return cart;
  }

  function directExpected(transaction, replacement) {
    const original = planner.identify(transaction.before, transaction.originalKey);
    const services = linkedServices(transaction.before, original);
    const serviceKeys = new Set(services.map(item => item.key));
    return transaction.before.items.map(line => {
      if (line.key === original.key) return { ref: planner.reference(replacement), quantity: transaction.quantity };
      const ref = planner.reference(line);
      if (serviceKeys.has(line.key)) {
        ref.properties['_DaLuca parent variant'] = String(replacement.variant_id);
        ref.properties['_DaLuca parent options'] = serviceSignature(replacement.properties);
      }
      return { ref, quantity: line.quantity };
    });
  }

  async function finish(transaction, epoch) {
    transaction = assertCurrent(transaction, epoch);
    clearRecord(transaction);
    saving = false;
    hideDialog();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'daluca-cart-edit' } }));
    const cartUrl = new URL(text.cartUrl, location.origin);
    if (location.pathname.replace(/\/$/, '') !== cartUrl.pathname.replace(/\/$/, '')) {
      cartUrl.searchParams.set('dl_edited', '1');
      location.assign(cartUrl.pathname + cartUrl.search);
      return;
    }
    sessionStorage.setItem('daluca-cart-edit-success', '1');
    location.reload();
  }

  function lineMoney(line) {
    const total = Number(line.original_line_price ??
      Number(line.original_price ?? line.price) * Number(line.quantity));
    if (!Number.isSafeInteger(total) || total < 0) throw error('UPDATE_UNCONFIRMED');
    return total;
  }

  function assertDirectMoney(transaction, cart) {
    const replacement = verifyReplacement(cart, transaction, false);
    if (!replacement) throw error('UPDATE_UNCONFIRMED');
    if (cart.currency !== transaction.before.currency) throw error('CURRENCY_CHANGED');
    const expected = (transaction.basePriceCents + transaction.feeCents) * transaction.quantity;
    const group = replacement.properties?._gpo_product_group;
    let fees = 0;
    if (group) {
      const parentQuantity = cart.items.filter(line =>
        String(line.properties?._gpo_product_group || '') === String(group) && !line.properties?._gpo_parent_product_group)
        .reduce((sum, line) => sum + Number(line.quantity), 0);
      const feeTotal = cart.items.filter(line =>
        String(line.properties?._gpo_parent_product_group || '') === String(group))
        .reduce((sum, line) => sum + lineMoney(line), 0);
      const numerator = feeTotal * transaction.quantity;
      if (!Number.isSafeInteger(parentQuantity) || parentQuantity <= 0 ||
          !Number.isSafeInteger(numerator)) throw error('UPDATE_UNCONFIRMED');
      fees = numerator / parentQuantity;
    }
    if (!Number.isSafeInteger(transaction.basePriceCents) || transaction.basePriceCents < 0 ||
        !Number.isSafeInteger(transaction.feeCents) || transaction.feeCents < 0 ||
        !Number.isSafeInteger(expected) || !Number.isSafeInteger(fees) || fees < 0 ||
        lineMoney(replacement) + fees !== expected) throw error('UPDATE_UNCONFIRMED');
    const original = planner.identify(transaction.before, transaction.originalKey);
    linkedServices(transaction.before, original).forEach(service => {
      const desired = { ...planner.reference(service), key: '', properties: {
        ...service.properties,
        '_DaLuca parent variant': String(replacement.variant_id),
        '_DaLuca parent options': serviceSignature(replacement.properties)
      } };
      const current = planner.identify(cart, desired);
      if (!current || lineMoney(current) !== lineMoney(service)) throw error('UPDATE_UNCONFIRMED');
    });
  }

  function assertRollbackMoney(transaction, cart) {
    if (cart.currency !== transaction.before.currency) throw error('CURRENCY_CHANGED');
    const totals = items => {
      const result = new Map();
      items.forEach(line => {
        const key = planner.fingerprint(line);
        const total = (result.get(key) || 0) + lineMoney(line);
        if (!Number.isSafeInteger(total)) throw error('UPDATE_UNCONFIRMED');
        result.set(key, total);
      });
      return result;
    };
    const before = totals(transaction.before.items);
    const current = totals(cart.items);
    if (before.size !== current.size) throw error('UPDATE_UNCONFIRMED');
    before.forEach((total, key) => {
      if (current.get(key) !== total) throw error('UPDATE_UNCONFIRMED');
    });
  }

  async function recoverDirect(transaction, epoch) {
    let cart = await transactionRequest(transaction, epoch, 'cart.js');
    transaction = assertCurrent(transaction, epoch);
    checkedServices(transaction.before, planner.identify(transaction.before, transaction.originalKey), true);
    let replacement = verifyReplacement(cart, transaction, false);
    if (!replacement) {
      const original = planner.identify(transaction.before, transaction.originalKey);
      const current = planner.identify(cart, planner.reference(original));
      if (!current) throw error('UPDATE_UNCONFIRMED');
      planner.assertExpected(cart, transaction.before.items.map(line => ({ ref: planner.reference(line), quantity: line.quantity })));
      if (transaction.definitiveRejected) {
        clearRecord(transaction);
        saving = false;
        restored = false;
        hideDialog();
        visibleMessage(text.failed);
        applyUi();
        if (!editKey) { sessionStorage.setItem('daluca-cart-edit-failed', '1'); location.reload(); }
        return;
      }
      try {
        await transactionRequest(transaction, epoch, 'cart/change.js', { id: current.key, quantity: transaction.quantity, properties: transaction.properties });
      } catch (failure) {
        if (failure.code === 'STALE_TRANSACTION') throw failure;
        if (failure.definitive) { transaction.definitiveRejected = true; persist(transaction); }
      }
      cart = await transactionRequest(transaction, epoch, 'cart.js');
      replacement = verifyReplacement(cart, transaction, false);
      if (!replacement) {
        if (transaction.definitiveRejected) return recoverDirect(transaction, epoch);
        throw error('UPDATE_UNCONFIRMED');
      }
    }
    cart = await relinkServices(transaction, cart, replacement, epoch);
    assertCurrent(transaction, epoch);
    planner.assertExpected(cart, directExpected(transaction, replacement));
    assertDirectMoney(transaction, cart);
    await finish(transaction, epoch);
  }

  function ownedRollbackPlan(transaction, cart, allowRejected) {
    if (transaction.phase === 'direct') throw error('ROLLBACK_UNCERTAIN');
    const owned = cart.items.some(line => line.properties?.[tokenKey] === transaction.token);
    if (!owned && !(allowRejected && (transaction.addRejected || transaction.phase === 'rollback'))) throw error('ROLLBACK_UNCERTAIN');
    return planner.planRollback(parentCart(transaction, transaction.before, false), parentCart(transaction, cart, false), transaction.originalKey, transaction.token);
  }

  async function rollback(transaction, cart, epoch, allowRejected) {
    transaction = assertCurrent(transaction, epoch);
    const plan = ownedRollbackPlan(transaction, cart, allowRejected);
    persist({ ...transaction, phase: 'rollback', plan });
    if (Object.keys(plan.updates).length) {
      try { await transactionRequest(transaction, epoch, 'cart/update.js', { updates: plan.updates }); }
      catch (failure) { if (failure.code === 'STALE_TRANSACTION') throw failure; }
    }
    const actual = await transactionRequest(transaction, epoch, 'cart.js');
    planner.assertExpected(parentCart(transaction, actual, false), plan.expected);
    assertRollbackMoney(transaction, actual);
    assertCurrent(transaction, epoch);
    clearRecord(transaction);
    saving = false;
    hideDialog();
    restored = false;
    visibleMessage(text.failed);
    applyUi();
    if (!editKey) {
      sessionStorage.setItem('daluca-cart-edit-failed', '1');
      location.reload();
    }
  }

  function recoverCommittedPlan(transaction, cart) {
    if (transaction.phase === 'direct' || transaction.phase === 'rollback') return null;
    const original = planner.identify(transaction.before, transaction.originalKey);
    if (!original || planner.identify(cart, planner.reference(original))) return null;
    if (!replacementFor(cart, transaction)) return null;
    const virtual = compactCart(cart);
    virtual.items.push(JSON.parse(JSON.stringify(original)));
    const group = original.properties?._gpo_product_group;
    if (group) {
      const parents = transaction.before.items.filter(line =>
        String(line.properties?._gpo_product_group || '') === String(group) && !line.properties?._gpo_parent_product_group);
      const parentQuantity = parents.reduce((sum, line) => sum + Number(line.quantity), 0);
      if (!Number.isSafeInteger(parentQuantity) || parentQuantity < Number(original.quantity)) return null;
      for (const child of transaction.before.items.filter(line => String(line.properties?._gpo_parent_product_group || '') === String(group))) {
        const numerator = Number(child.quantity) * Number(original.quantity);
        if (!Number.isSafeInteger(numerator) || numerator % parentQuantity !== 0) return null;
        const share = numerator / parentQuantity;
        const current = planner.identify(virtual, planner.reference(child), true);
        if (current) {
          current.quantity += share;
          current.original_line_price = Number(current.original_price ?? current.price) * current.quantity;
        } else {
          virtual.items.push({ ...JSON.parse(JSON.stringify(child)), quantity: share,
            original_line_price: Number(child.original_price ?? child.price) * share });
        }
      }
    }
    if (!verifyReplacement(virtual, transaction, true)) return null;
    const plan = planner.planCommit(parentCart(transaction, transaction.before, false), parentCart(transaction, virtual), transaction.originalKey, transaction.token);
    planner.assertCommitted(parentCart(transaction, transaction.before, false), parentCart(transaction, cart), plan, transaction.originalKey);
    return plan;
  }

  async function finishCommitted(transaction, cart, epoch) {
    transaction = assertCurrent(transaction, epoch);
    let plan = transaction.plan?.kind === 'commit' ? transaction.plan : null;
    try {
      const verified = recoverCommittedPlan(transaction, cart);
      if (!verified) return false;
      if (!plan) plan = verified;
      planner.assertCommitted(parentCart(transaction, transaction.before, false), parentCart(transaction, cart), plan, transaction.originalKey);
      if (transaction.plan?.kind !== 'commit') persist({ ...transaction, phase: 'committing', plan });
      const replacement = replacementFor(cart, transaction);
      cart = await relinkServices(transaction, cart, replacement, epoch);
      if (!recoverCommittedPlan(transaction, cart)) return false;
      planner.assertCommitted(parentCart(transaction, transaction.before, false), parentCart(transaction, cart), plan, transaction.originalKey);
    } catch (failure) {
      if (failure.code === 'STALE_TRANSACTION') throw failure;
      return false;
    }
    assertCurrent(transaction, epoch);
    await finish(transaction, epoch);
    return true;
  }

  async function recoverReplacement(transaction, epoch) {
    let cart = await transactionRequest(transaction, epoch, 'cart.js');
    transaction = assertCurrent(transaction, epoch);
    if (transaction.phase === 'rollback') {
      try {
        planner.assertExpected(parentCart(transaction, cart, false), transaction.plan.expected);
        assertRollbackMoney(transaction, cart);
        clearRecord(transaction);
        saving = false;
        hideDialog();
        sessionStorage.setItem('daluca-cart-edit-failed', '1');
        location.assign(text.cartUrl);
        return;
      } catch (_) {
        await rollback(transaction, cart, epoch, true);
        return;
      }
    }
    if (await finishCommitted(transaction, cart, epoch)) return;
    transaction = assertCurrent(transaction, epoch);
    if (transaction.basePriceCents == null) {
      const base = catalogBasePrice(transaction.productId, transaction.variantId, transaction.before.currency);
      if (base !== null) {
        transaction = { ...transaction, basePriceCents: base };
        persist(transaction);
      }
    }
    const deadline = Date.now() + 24000;
    let replacement = null;
    while (Date.now() < deadline) {
      transaction = assertCurrent(transaction, epoch);
      if (transaction.addRejected) { await rollback(transaction, cart, epoch, true); return; }
      replacement = verifyReplacement(cart, transaction, true);
      if (replacement) break;
      await pause(600);
      cart = await transactionRequest(transaction, epoch, 'cart.js');
    }
    if (!replacement) {
      const nativeErrors = preparedFormMatches(context?.form, transaction) &&
        context?.adapter.getRoot()?.querySelector('.gpo-element.has-error');
      if (nativeErrors) {
        transaction.addRejected = true;
        persist(transaction);
        await rollback(transaction, cart, epoch, true);
        return;
      }
      throw error('UPDATE_UNCONFIRMED');
    }
    cart = await transactionRequest(transaction, epoch, 'cart.js');
    if (!verifyReplacement(cart, transaction, true)) throw error('UPDATE_UNCONFIRMED');
    const plan = planner.planCommit(parentCart(transaction, transaction.before, false), parentCart(transaction, cart), transaction.originalKey, transaction.token);
    assertCurrent(transaction, epoch);
    persist({ ...transaction, phase: 'committing', plan });
    try { await transactionRequest(transaction, epoch, 'cart/update.js', { updates: plan.updates }); }
    catch (failure) { if (failure.code === 'STALE_TRANSACTION') throw failure; }
    cart = await transactionRequest(transaction, epoch, 'cart.js');
    try {
      planner.assertCommitted(parentCart(transaction, transaction.before, false), parentCart(transaction, cart), plan, transaction.originalKey);
    } catch (_) {
      if (!planner.identify(cart, plan.original)) throw error('UPDATE_UNCONFIRMED');
      throw error('COMMIT_UNCONFIRMED');
    }
    if (!await finishCommitted(transaction, cart, epoch)) throw error('UPDATE_UNCONFIRMED');
  }

  async function resume(transaction, options = {}) {
    if (recovery) return recovery.promise;
    try { transaction = assertCurrent(transaction); }
    catch (failure) {
      if (failure.code === 'STALE_TRANSACTION') { reconcileJournal(); return false; }
      throw failure;
    }
    const run = { epoch: pageEpoch, token: transaction.token };
    recovery = run;
    saving = true;
    canKeepOriginal = false;
    if (options.interactive || !transaction.dismissed) showDialog(options.keepOriginal ? text.restoring : text.checking, false);
    syncRecoveryUi();
    run.promise = (async () => {
      try {
        if (options.keepOriginal) {
          const cart = await transactionRequest(transaction, run.epoch, 'cart.js');
          transaction = assertCurrent(transaction, run.epoch);
          if (await finishCommitted(transaction, cart, run.epoch)) return;
          await rollback(transaction, cart, run.epoch, false);
        } else if (transaction.phase === 'direct') await recoverDirect(transaction, run.epoch);
        else await recoverReplacement(transaction, run.epoch);
      } catch (failure) {
        if (failure.code === 'STALE_TRANSACTION') {
          if (run.epoch === pageEpoch) {
            if (recovery === run) recovery = null;
            reconcileJournal();
          }
          return;
        }
        console.warn('[DaLuca cart edit] Update verification:', failure.code || failure.name || 'UNKNOWN');
        try {
          const cart = await transactionRequest(transaction, run.epoch, 'cart.js');
          transaction = assertCurrent(transaction, run.epoch);
          if (await finishCommitted(transaction, cart, run.epoch)) return;
          if (!options.keepOriginal && transaction.phase !== 'direct' && failure.code !== 'UPDATE_UNCONFIRMED' && failure.name !== 'AbortError') {
            await rollback(transaction, cart, run.epoch, true);
            return;
          }
          try { ownedRollbackPlan(transaction, cart, false); canKeepOriginal = true; }
          catch (_) { canKeepOriginal = false; }
        } catch (followup) {
          if (followup.code === 'STALE_TRANSACTION') {
            if (run.epoch === pageEpoch) {
              if (recovery === run) recovery = null;
              reconcileJournal();
            }
            return;
          }
        }
        assertCurrent(transaction, run.epoch);
        recoveryUncertain = true;
        if (options.interactive || !record.dismissed) showDialog(text.uncertain, true);
        else hideDialog();
        syncRecoveryUi();
      } finally {
        if (recovery === run) {
          recovery = null;
          syncRecoveryUi();
        }
      }
    })();
    return run.promise;
  }

  function accessoryActionsPending() {
    if (context?.adapter.hasPendingAccessories?.()) return true;
    const scopes = document.querySelectorAll('#main-content [data-daluca-accessory-addon-group],#main-content [data-daluca-care-proxy],#main-content .daluca-care-proxy-v35,#main-content [data-daluca-care-proxy-grid],#main-content .daluca-pdp-upsell,#main-content .daluca-ready-addon-row');
    const busy = '.loading,.is-loading,.is-busy,[aria-busy="true"]';
    return Array.from(scopes).some(scope => {
      const controls = [scope, ...scope.querySelectorAll('button,input[type="submit"],[role="button"]')];
      return controls.some(control => control.matches(busy) ||
        (control.matches('button,input[type="submit"],[role="button"]') &&
          /\b(adding|removing)\b/i.test(window.DaLucaI18n.sourceText(control) || control.value || '')));
    });
  }

  async function waitForAccessoryActions() {
    context?.adapter.prepareAccessories?.();
    const deadline = Date.now() + 15000;
    while (accessoryActionsPending()) {
      if (Date.now() >= deadline) throw error('ADDON_BUSY');
      await pause(100);
    }
  }

  async function save(descriptor) {
    if (saving || !restored || !context || descriptor.form !== context.form) return false;
    const epoch = pageEpoch;
    let transaction = null;
    saving = true;
    showDialog(text.saving, false);
    try {
      assertIdle(epoch);
      await waitForAccessoryActions();
      assertIdle(epoch);
      const fresh = await request('cart.js');
      assertIdle(epoch);
      const original = planner.identify(fresh, context.original);
      if (!original) throw error('CART_CHANGED');
      const snapshot = context.adapter.snapshot();
      const variantId = String(descriptor.variantId || snapshot.variantId || '');
      const status = descriptor.form.dataset.dalucaVariantStatus;
      if (!variantId || (status && status !== 'ready') || snapshot.pricingKnown === false || snapshot.valid === false) throw error('INVALID_CHOICES');
      const feeCents = Math.round(Number(descriptor.paidAmount ?? snapshot.paidAmount ?? 0) * 100);
      if (!Number.isSafeInteger(feeCents) || feeCents < 0) throw error('INVALID_CHOICES');
      const basePriceCents = catalogBasePrice(original.product_id, variantId, fresh.currency);
      if (basePriceCents === null) throw error('INVALID_CHOICES');
      const properties = mergedProperties(original, { ...snapshot, ...descriptor }, descriptor.form);
      const token = crypto.randomUUID();
      properties[tokenKey] = token;
      const nativeFee = feeSignature(formProperties(descriptor.form));
      const sameFees = context.feeSignature !== null && nativeFee === context.feeSignature &&
        feeCents === context.feeCents && originalFee(fresh, original) === feeCents &&
        (feeCents === 0 || (!!nativeFee && nativeFee === feeSignature(original.properties)));
      const direct = variantId === String(original.variant_id) && sameFees;
      checkedServices(fresh, original, direct);
      if (direct && original.properties?._gpo_product_group) {
        properties._gpo_product_group = original.properties._gpo_product_group;
        if (original.properties._gpo_addon_products) properties._gpo_addon_products = original.properties._gpo_addon_products;
      }
      const managed = new Set(descriptor.managedKeys || snapshot.managedKeys || []);
      const verificationProperties = Object.fromEntries(Object.entries(properties).filter(([key]) => key[0] !== '_' && !managed.has(key)));
      Object.assign(verificationProperties, descriptor.properties || snapshot.properties || {});
      transaction = {
        version: 1, createdAt: Date.now(), token, originalKey: original.key,
        before: compactCart(fresh), productId: String(original.product_id), variantId,
        quantity: Number(original.quantity), feeCents, basePriceCents, properties, verificationProperties,
        propertyAliases: snapshot.propertyAliases || {},
        phase: direct ? 'direct' : 'adding'
      };
      recoveryUncertain = false;
      persist(transaction, true);
      if (direct) {
        try {
          await transactionRequest(transaction, epoch, 'cart/change.js', { id: original.key, quantity: original.quantity, properties });
        } catch (failure) {
          if (failure.code === 'STALE_TRANSACTION') throw failure;
          if (failure.definitive) {
            transaction.definitiveRejected = true;
            persist(transaction);
          }
        }
        await resume(transaction);
        return true;
      }
      assertCurrent(transaction, epoch);
      prepareForm(descriptor.form, properties, token, original.quantity);
      const started = await descriptor.submit();
      transaction = assertCurrent(transaction, epoch);
      if (started === false && feeCents > 0) {
        const cart = await transactionRequest(transaction, epoch, 'cart.js');
        if (!replacementFor(cart, transaction)) {
          clearRecord(transaction);
          throw error('INVALID_CHOICES');
        }
      }
      await resume(transaction);
      return true;
    } catch (failure) {
      if (failure.code === 'STALE_TRANSACTION') {
        if (epoch === pageEpoch) reconcileJournal();
        return false;
      }
      if (transaction && record) {
        if (failure.code === 'UNSUPPORTED_LINKED_UPGRADE') clearRecord(transaction);
        else {
          await resume(transaction);
          return false;
        }
      }
      saving = false;
      hideDialog();
      visibleMessage(failure.code === 'STORAGE' ? text.storage :
        failure.code === 'CART_CHANGED' ? text.changed :
        failure.code === 'LINKED_SERVICE_QUANTITY_MISMATCH' ? (text.linkedServiceQuantity || 'Return to your cart and update the strap quantity before editing this item.') :
        failure.code === 'ADDON_BUSY' ? text.accessoriesBusy :
        failure.code === 'INVALID_CHOICES' ? text.invalid : text.failed);
      if (failure.code !== 'INVALID_CHOICES' && failure.code !== 'ADDON_BUSY') restored = false;
      applyUi();
      return false;
    }
  }

  function nativeButton() {
    return context?.form.querySelector('button[name="add"],button[type="submit"],input[type="submit"]');
  }

  function triggerSave() {
    if (!context || !restored || saving) return;
    if (context.custom) {
      context.adapter.getBuilder()?.querySelector('.daluca-proxy-add-to-cart')?.click();
      return;
    }
    const button = nativeButton();
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true' ||
        !context.form.reportValidity() ||
        (window.DaLucaQuickReleaseConsent && !window.DaLucaQuickReleaseConsent.validate())) {
      visibleMessage(text.invalid);
      return;
    }
    if (window.DaLucaFinderHandoff && !window.DaLucaFinderHandoff.validateProductForm(context.form)) return;
    const snapshot = context.adapter.snapshot();
    save({ ...snapshot, form: context.form, submit: async () => { button.click(); return true; } });
  }

  function actions() {
    const row = document.createElement('div');
    row.className = 'daluca-cart-edit-actions';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button';
    button.dataset.dalucaEditSave = 'true';
    window.DaLucaI18n.setText(button, text.save);
    button.disabled = !restored || saving;
    button.addEventListener('click', triggerSave);
    const cancel = document.createElement('a');
    cancel.href = text.cartUrl;
    window.DaLucaI18n.setText(cancel, text.cancel);
    row.append(button, cancel);
    return row;
  }

  function placeEditor() {
    const main = document.getElementById('main-content');
    if (!main || !banner || !context) return;
    const form = context.form;
    const builder = context.custom ? context.adapter.getBuilder() : null;
    const checkout = builder?.querySelector('.daluca-custom-builder-live__checkout');
    const holder = nativeButton()?.closest('.add_to_cart_holder');
    const target = checkout || form?.closest('.daluca-accessory-order-step--purchase') ||
      holder?.parentElement || form?.closest('.product-form') || form?.parentElement;
    if (!target?.isConnected || banner.contains(target)) return;
    if (!purchase) {
      purchase = document.createElement('div');
      purchase.className = 'dl-launch-edit-purchase';
      purchase.appendChild(banner);
      purchase.appendChild(actions());
    }
    if (purchase.parentElement !== target) target.appendChild(purchase);
    if (banner.parentElement !== purchase) purchase.prepend(banner);
    banner.querySelectorAll('.daluca-cart-edit-actions').forEach(row => row.remove());
    main.querySelectorAll('[data-dl-launch-retired-holder]').forEach(holder => holder.removeAttribute('data-dl-launch-retired-holder'));
  }

  function applyUi() {
    if (!context) return;
    const form = context.adapter.getForm();
    if (form) context.form = form;
    placeEditor();
    const optionRegions = context.custom
      ? context.adapter.getBuilder()?.querySelectorAll('.daluca-custom-builder-live__options')
      : context.adapter.getRoot()?.querySelectorAll('.gpo-app,variant-selects,variant-radios');
    optionRegions?.forEach(region => {
      if (restoringChoices) {
        if (!region.hasAttribute('inert')) {
          region.setAttribute('inert', '');
          region.setAttribute('data-daluca-edit-restoring', '');
        }
      } else if (region.hasAttribute('data-daluca-edit-restoring')) {
        region.removeAttribute('inert');
        region.removeAttribute('data-daluca-edit-restoring');
      }
    });
    document.querySelectorAll('[data-daluca-edit-save]').forEach(button => {
      const next = !restored || saving;
      if (button.disabled !== next) button.disabled = next;
    });
    let servicesChanged = false;
    if (!context.custom) Array.from(context.form?.elements || []).filter(input => input.name === 'quantity').forEach(input => {
      if (String(input.value) !== String(context.original.quantity)) { input.value = String(context.original.quantity); servicesChanged = true; }
    });
    context.adapter.getRoot()?.querySelectorAll('[data-daluca-strap-upgrade-toggle],[data-daluca-quick-release-toggle]').forEach(input => {
      const preserved = !context.custom && input.dataset.addonKey === 'quick-release' &&
        (context.services || []).some(service => String(service.variant_id) === String(input.dataset.variantId));
      if (input.checked !== preserved) { input.checked = preserved; servicesChanged = true; }
      if (!input.disabled) { input.disabled = true; servicesChanged = true; }
      input.dataset.dalucaEditServicePreserved = preserved ? 'true' : 'false';
      const consent = document.querySelector('[data-daluca-qr-consent]');
      input.dataset.dalucaEditConsentValue = preserved && consent ? String(context.original.properties?.[consent.dataset.property] || '') : '';
      const card = input.closest('.daluca-master-addon-card-v366,label');
      let note = card?.querySelector('[data-daluca-edit-preserved-service]');
      if (preserved && !note) {
        note = document.createElement('small');
        note.dataset.dalucaEditPreservedService = 'true';
        note.setAttribute('role', 'status');
        window.DaLucaI18n.setText(note, text.preservedService || 'Already included with this cart item. It stays selected while you edit.');
        (card.querySelector('.daluca-master-addon-card-v366__copy') || card).appendChild(note);
      } else if (!preserved && note) note.remove();
    });
    if (servicesChanged) document.dispatchEvent(new CustomEvent('daluca:cart-edit-services'));
  }

  function observeUi() {
    uiObserver?.disconnect();
    uiObserver = new MutationObserver(() => {
      if (uiQueued) return;
      uiQueued = true;
      requestAnimationFrame(() => { uiQueued = false; applyUi(); });
    });
    const main = document.getElementById('main-content');
    if (main) uiObserver.observe(main, { subtree: true, childList: true });
  }

  async function initialize() {
    const epoch = pageEpoch;
    restored = false;
    restoringChoices = false;
    context = null;
    uiObserver?.disconnect();
    purchase?.remove();
    purchase = null;
    banner?.remove();
    document.documentElement.setAttribute('data-daluca-cart-editing', 'loading');
    banner = document.createElement('section');
    banner.className = 'daluca-cart-edit-banner';
    const heading = document.createElement('h2');
    window.DaLucaI18n.setText(heading, text.title);
    const copy = document.createElement('p');
    copy.dataset.dalucaEditMessage = 'true';
    copy.setAttribute('role', 'status');
    window.DaLucaI18n.setText(copy, text.loading);
    const help = document.createElement('p');
    window.DaLucaI18n.setText(help, text.accessories);
    banner.append(heading, copy, help, actions());
    const accessoryMount = document.querySelector('#main-content .daluca-accessory-purchase .product-information--inner');
    (accessoryMount || document.getElementById('main-content'))?.prepend(banner);
    try {
      assertIdle(epoch);
      const cart = await request('cart.js');
      assertIdle(epoch);
      const original = planner.identify(cart, editKey);
      if (!original || original.selling_plan_allocation || original.properties?._gpo_parent_product_group) throw error('UNAVAILABLE');
      const deadline = Date.now() + 25000;
      let adapter = null;
      let custom = false;
      const customRoot = document.querySelector('.daluca-pdp--custom[data-daluca-consolidated-custom="true"],[data-daluca-custom-builder-mount],.daluca-custom-consolidated-builder');
      while (Date.now() < deadline) {
        assertIdle(epoch);
        if (window.DaLucaCustomCartAdapter?.isReady()) {
          adapter = window.DaLucaCustomCartAdapter;
          custom = true;
          break;
        }
        if (!customRoot && window.DaLucaNativeCartAdapter?.isReady()) {
          adapter = window.DaLucaNativeCartAdapter;
          break;
        }
        await pause(150);
      }
      if (!adapter) throw error('UNAVAILABLE');
      const form = adapter.getForm();
      const productId = form.querySelector('[name="product-id"]')?.value ||
        adapter.getRoot()?.dataset.dalucaMainProductId || '';
      if (String(productId) !== String(original.product_id)) throw error('UNAVAILABLE');
      context = { adapter, custom, form, original: planner.reference(original), services: linkedServices(cart, original) };
      observeUi();
      restoringChoices = true;
      applyUi();
      let result;
      try {
        result = await adapter.restore(original.properties || {});
      } finally {
        if (epoch === pageEpoch) {
          restoringChoices = false;
          applyUi();
        }
      }
      assertIdle(epoch);
      if (!result.ok) {
        visibleMessage(text.missing.replace('__FIELDS__', result.missing.join(', ')));
        return;
      }
      const snapshot = adapter.snapshot();
      if (String(snapshot.variantId) !== String(original.variant_id) || snapshot.pricingKnown === false) throw error('UNAVAILABLE');
      context.feeSignature = feeSignature(formProperties(form));
      context.feeCents = Math.round(Number(snapshot.paidAmount || 0) * 100);
      restored = true;
      document.documentElement.setAttribute('data-daluca-cart-editing', 'ready');
      visibleMessage(text.ready);
      applyUi();
    } catch (failure) {
      if (failure.code === 'STALE_TRANSACTION') {
        if (epoch === pageEpoch) reconcileJournal();
        return;
      }
      visibleMessage(text.unavailable);
    }
  }

  window.DaLucaCartEditor = {
    refreshUi: applyUi,
    isEditing(form) { return !!editKey && !!context && context.form === form; },
    isSaving(form) { return saving && (!context || context.form === form); },
    submissionRejected(form) {
      const current = readRecord();
      if (!saving || !context || context.form !== form || current?.phase !== 'adding' || current.token !== record?.token ||
          !preparedFormMatches(form, current)) return;
      persist({ ...current, addRejected: true });
    },
    save
  };

  function handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const current = readRecord();
    if (current) record = current;
    else if (record) reconcileJournal();
    const ownAdd = current && current.phase === 'adding' && context?.form === form && saving && !recoveryUncertain &&
      preparedFormMatches(form, current);
    if (current && !ownAdd && (form.id === 'cart-form' || form.querySelector('[name="checkout"]') ||
        isCheckoutUrl(form.action) || /\/cart(?:\/(?:add|change|update)(?:\.js)?)?\/?$/.test(new URL(form.action, location.href).pathname))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      resume(current, { interactive: true });
      return;
    }
    if (!editKey || saving || !(form instanceof HTMLFormElement) ||
        !form.closest('#main-content') || !form.action.includes('/cart/add')) return;
    if (context && form !== context.form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (restored) triggerSave();
  }

  function handleClick(event) {
    const link = event.target.closest('a[href]');
    const guarded = event.target.closest(checkoutSelector + ',' + cartMutationSelector + (recoveryUncertain ? ',' + additionSelector : ''));
    if (!guarded && !(link && isCheckoutUrl(link.href))) return;
    const current = readRecord();
    if (current) {
      record = current;
      event.preventDefault();
      event.stopImmediatePropagation();
      resume(current, { interactive: true });
    }
  }

  function pageHidden() {
    pageEpoch += 1;
    restored = false;
    recovery = null;
    activeRequests.forEach(controller => controller.abort());
    activeRequests.clear();
  }

  function pageShown(event) {
    if (!event.persisted) return;
    pageEpoch += 1;
    recovery = null;
    if (editKey && !readRecord()) {
      saving = false;
      initialize();
    } else reconcileJournal();
  }

  window.addEventListener('submit', handleSubmit, true);
  document.addEventListener('click', handleClick, true);
  window.addEventListener('pagehide', pageHidden);
  window.addEventListener('pageshow', pageShown);
  new MutationObserver(() => {
    if (!record || guardQueued) return;
    guardQueued = true;
    requestAnimationFrame(() => { guardQueued = false; syncRecoveryUi(); });
  }).observe(document.body, { subtree: true, childList: true });

  record = readRecord();
  if (record) {
    recoveryUncertain = !!record.dismissed;
    syncRecoveryUi();
    resume(record);
  } else if (editKey) {
    initialize();
  } else {
    let succeeded = new URLSearchParams(location.search).get('dl_edited') === '1';
    let failed = false;
    try {
      succeeded = succeeded || sessionStorage.getItem('daluca-cart-edit-success') === '1';
      failed = sessionStorage.getItem('daluca-cart-edit-failed') === '1';
      sessionStorage.removeItem('daluca-cart-edit-success');
      sessionStorage.removeItem('daluca-cart-edit-failed');
    } catch (_) {}
    if (succeeded || failed) {
      const note = document.createElement('p');
      note.className = 'daluca-cart-edit-success';
      note.setAttribute('role', 'status');
      window.DaLucaI18n.setText(note, succeeded ? text.saved : text.failed);
      document.querySelector('#Cart')?.prepend(note);
      const url = new URL(location.href);
      url.searchParams.delete('dl_edited');
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    }
  }
})();





